import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { Section } from '@packages/ui/layout';
import { Surface } from '@packages/ui/layout';
import { EmptyState } from '@packages/ui/layout/EmptyState';
import { FormRenderer } from '@packages/form-engine/FormRenderer';
import { loadViewSchema } from '@packages/form-engine/resolvers/ViewLoader';
import { createDefaultContext } from '@packages/form-engine/resolvers/createDefaultContext';
import type { ViewSchemaFiles } from '@packages/form-engine/resolvers/types';
import type { FormSchema } from '@packages/form-engine/types/schema';
import type { ViewBlock, ViewBlockDataSource, BlockRendererSharedProps } from '../types/blocks';
import { getViewLoader } from '../registry/loaderRegistry';
import { interpolateBlockParams } from '../registry/interpolateBlockParams';
import { ActionRegistry } from '@packages/table-builder/registry';
import { BlockLoadState } from './BlockLoadState';
import { useAsyncBlockResource } from './useAsyncBlockResource';
import { useViewSchemaVersion } from './useSchemaVersion';
import { useStudioPreview } from '../core/StudioPreviewContext';
import { isStudioPreviewEntityId, resolveStudioBlockEntityId } from '../core/studioPreviewEntity';
import { useViewEventAdapter } from '@packages/block-events/react/adapters/useViewEventAdapter';

export interface ViewBlockRendererProps extends BlockRendererSharedProps {
  block: ViewBlock;
}

export function ViewBlockRenderer({
  block,
  params,
  meta,
  onSuccess,
  onError,
  onCancel,
  inheritedDataSource,
}: ViewBlockRendererProps): JSX.Element {
  const skeletonOnFetch = block.skeletonOnFetch ?? false;
  const presentation = block.presentation ?? 'default';
  const surfacePadding = block.surfacePadding ?? '1.8rem 3.6rem 0 3.6rem';
  const { isStudioPreview, previewDataMode, mockDataBag, resolveMockData } = useStudioPreview();

  const [refreshGen, bumpRefresh] = useReducer((n: number) => n + 1, 0);
  const viewAdapterOptions = useMemo(() => ({
    onExternalRefetch: () => bumpRefresh(),
  }), []);
  const viewAdapter = useViewEventAdapter(block.semanticId, viewAdapterOptions);

  const viewLoadedDataRef = useRef<unknown>(undefined);
  useEffect(() => {
    if (block.data != null && block.data !== viewLoadedDataRef.current) {
      viewLoadedDataRef.current = block.data;
      viewAdapter.publishViewLoaded(block.data as Record<string, unknown>);
    }
  }, [block.data, viewAdapter]);

  const isUnconfigured = !block.$id || block.$id === '';

  const loader = useCallback(async () => {
    const viewLoader = getViewLoader();
    if (!viewLoader) {
      throw new Error(
        `[ViewBlockRenderer] No view loader registered. ` +
        `Ensure "@packages/domain" is imported before rendering.`,
      );
    }

    const domainMatch = block.$id.match(/^wealthlane:\/\/views\/([^/#]+)/);
    if (domainMatch) {
      await ActionRegistry.ensureDomain(domainMatch[1]);
    }

    const schema = await viewLoader(block.$id);
    if (!schema) {
      throw new Error(
        `[ViewBlockRenderer] No view schema found for block "${block.$id}". ` +
        `Check that a matching *.model.json with $id exists in @packages/domain/modules/.`,
      );
    }

    const files: ViewSchemaFiles = {
      domain: schema.domain as ViewSchemaFiles['domain'],
      orch: schema.orch as ViewSchemaFiles['orch'],
    };

    const result = loadViewSchema(files, createDefaultContext());
    return { ...result, rawDomain: schema.domain as Record<string, unknown> };
  }, [block.$id]);

  const viewVersion = useViewSchemaVersion();

  const resource = useAsyncBlockResource(
    isUnconfigured ? '__skip__' : `view-block:${block.$id}`,
    loader,
    viewVersion,
  );

  if (isUnconfigured) {
    return (
      <EmptyState
        icon='edit_note'
        title='View block not configured'
        description='Select this block in the inspector and choose a view schema.'
        semanticId='viewBlockUnconfigured'
      />
    );
  }

  const isLoading = resource.status === 'idle' || resource.status === 'loading';
  const error = resource.status === 'error' ? resource.error : null;

  const hasPrefetchedData = block.data != null;
  const resolvedEntityId = resolveStudioBlockEntityId(params, undefined, isStudioPreview);
  const resolvedParams = resolvedEntityId ? { ...params, id: resolvedEntityId } : params;
  const useStudioMockData = isStudioPreview
    && !isStudioPreviewEntityId(resolvedEntityId)
    && !hasPrefetchedData;

  // Inherited action from the parent stack/block.
  const inheritedActionId = useMemo(() => {
    if (block.fetchActionId) return block.fetchActionId;
    return block.dataSource?.fetch?.actionId
      ?? inheritedDataSource?.fetch?.actionId
      ?? null;
  }, [block.fetchActionId, block.dataSource, inheritedDataSource]);

  // The schema's own actionId (from domain.json root `actionId`). Null if not overridden.
  const schemaActionId = resource.status === 'ready'
    ? resource.data.schema.modes.view?.fetchActionId ?? null
    : null;

  // Final action: schema override wins, then inherited.
  const finalActionId = schemaActionId ?? inheritedActionId;

  // Does the schema declare a DIFFERENT action from the inherited one?
  // If so, the view needs a supplemental fetch even when initialValues is present.
  const schemaOverridesAction = schemaActionId != null && schemaActionId !== inheritedActionId;

  const effectiveDataSource = useMemo<ViewBlockDataSource | null>(() => {
    if (!finalActionId) {
      const rawDs = block.dataSource ?? inheritedDataSource ?? null;
      if (!rawDs) return null;
      if (!rawDs.fetch?.params) return rawDs;
      return {
        ...rawDs,
        fetch: {
          ...rawDs.fetch,
          params: interpolateBlockParams(rawDs.fetch.params, resolvedParams, meta),
        },
      };
    }

    if (isStudioPreview && !isStudioPreviewEntityId(resolvedEntityId)) {
      return null;
    }

    return {
      fetch: {
        actionId: finalActionId,
        params: { id: resolvedEntityId ?? '' },
      },
    };
  }, [finalActionId, block.dataSource, inheritedDataSource, resolvedParams, resolvedEntityId, meta, isStudioPreview]);

  const resolvedSchema = useMemo<FormSchema | null>(() => {
    if (resource.status !== 'ready') return null;
    const base = resource.data.schema;

    if (!effectiveDataSource) return base;

    return {
      ...base,
      modes: {
        ...base.modes,
        view: {
          ...base.modes.view,
          dataSource: effectiveDataSource as any,
          fetchActionId: undefined,
        },
      },
    };
  }, [resource, effectiveDataSource]);

  const rawDomain = resource.status === 'ready' ? resource.data.rawDomain : undefined;

  const studioMockValues = useMemo(() => {
    if (!useStudioMockData || previewDataMode !== 'mock') return undefined;
    if (mockDataBag && Object.keys(mockDataBag).length > 0) return mockDataBag;
    if (resolveMockData && rawDomain) {
      return resolveMockData(block.$id, rawDomain);
    }
    return {};
  }, [useStudioMockData, previewDataMode, mockDataBag, resolveMockData, rawDomain, block.$id]);

  const effectiveInitialValues = hasPrefetchedData ? block.data : studioMockValues;
  const shouldSuppressFetch = isStudioPreview && !isStudioPreviewEntityId(resolvedEntityId);

  const formRenderer = resolvedSchema != null ? (
    <FormRenderer
      key={refreshGen}
      schema={resolvedSchema}
      mode='view'
      params={resolvedParams}
      meta={meta}
      onSuccess={onSuccess}
      onError={onError}
      onCancel={onCancel}
      skeletonOnFetch={shouldSuppressFetch ? false : (hasPrefetchedData ? false : skeletonOnFetch)}
      initialValues={effectiveInitialValues}
      supplementalFetch={hasPrefetchedData && schemaOverridesAction}
    />
  ) : null;

  return (
    <BlockLoadState isLoading={isLoading} error={error}>
      {resolvedSchema != null && (
        presentation === 'surface' ? (
          <Surface
            tone='primary'
            padding={surfacePadding}
            semanticId={block.semanticId}
            className='mb-6'
            style={{ marginBottom: '24px', overflow: 'hidden' }}
          >
            <div style={{ marginBottom: '-20px' }}>
              {formRenderer}
            </div>
          </Surface>
        ) : (
          <Section title={block.title} semanticId={block.semanticId}>
            {formRenderer}
          </Section>
        )
      )}
    </BlockLoadState>
  );
}

